/**
 * JMI Motion Presets
 * ----------------------------------------------------------------------------
 * Central, theme-agnostic animation vocabulary for the whole app.
 * Built on `motion` (Motion for React, v12+). Import via `motion/react`.
 *
 * Design intent:
 *  - One set of variants used everywhere so motion feels consistent.
 *  - Subtle, premium "SaaS" feel: short distances, soft springs, no bounce.
 *  - Respects `prefers-reduced-motion` automatically via the <Reveal> wrapper.
 *  - Never depends on color tokens, so light/dark parity is automatic.
 */

// Signature easing — a gentle, confident deceleration.
export const EASE_OUT = [0.22, 1, 0.36, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];

// Spring used for interactive/pointer-driven motion (cards, buttons, tiles).
export const SPRING_SOFT = { type: 'spring', stiffness: 260, damping: 26, mass: 0.8 };
// Spring used for layout + entrance reveals (slightly more settle).
export const SPRING_REVEAL = { type: 'spring', stiffness: 180, damping: 24, mass: 1 };

// Distance kept small so reveals feel restrained, not theatrical.
const LIFT = 24;

export const variants = {
  /** Fade + rise. The default reveal for headings, paragraphs, cards. */
  fadeUp: {
    hidden: { opacity: 0, y: LIFT },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_OUT } },
  },
  /** Pure fade — for overlays, badges, strips that should not translate. */
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5, ease: EASE_OUT } },
  },
  /** Scale-in for hero badges, logos, icons. */
  scaleIn: {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE_OUT } },
  },
  /** Slide-in from the left — for split-section left columns. */
  slideLeft: {
    hidden: { opacity: 0, x: -LIFT },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE_OUT } },
  },
  /** Slide-in from the right — for split-section right columns. */
  slideRight: {
    hidden: { opacity: 0, x: LIFT },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE_OUT } },
  },
  /** Stagger container — children animate in sequence. */
  stagger: (stagger = 0.08, delayChildren = 0.05) => ({
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren },
    },
  }),
  /** Child of a stagger container — fades up. */
  staggerItem: {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } },
  },
};

// Viewport config for `whileInView` reveals. Once-only so scroll-back is clean.
export const VIEWPORT = { once: true, amount: 0.2, margin: '0px 0px -8% 0px' };

export const cubicBezierEases = { EASE_OUT, EASE_IN_OUT };
